import { IonIcon, IonLabel, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { Redirect, Route, type RouteComponentProps } from "react-router-dom";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { homeIcon as homeOutlineIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { homeIcon as homeSolidIcon } from "@/lib/icons/@heroicons/react/24/solid";
import { calendarIcon } from "@/lib/icons/calendar.icon";
import { chatFilledIcon, chatIcon } from "@/lib/icons/chat.icon";
import { completedFilledIcon } from "@/lib/icons/completed.filled.icon";
import { completedIcon } from "@/lib/icons/completed.icon";
import { settingsFilledIcon } from "@/lib/icons/settings.filled.icon";
import { settingsIcon } from "@/lib/icons/settings.icon";
import { Routes } from "@/lib/router";
import { CompletedShiftsPage } from "@/routes/app/completed/completed";
import { ConversationDetailPage } from "@/routes/app/conversations/[id]";
import { ConversationInfoPage } from "@/routes/app/conversations/[id]/info";
import { ConversationsPage } from "@/routes/app/conversations/conversations";
import { ProjectsPage } from "@/routes/app/project/projects";
import { SettingsPage } from "@/routes/app/settings";
import { UpcomingShiftsPage } from "@/routes/app/upcoming/upcoming";
import { ProjectRouter } from "./project.router";

interface TabRouterProps extends RouteComponentProps {
  isAdmin: boolean;
}

export function TabRouter(props: TabRouterProps) {
  const { t } = useTranslation();
  const PreferredHaptics = usePreferredHaptics();
  const { isAdmin } = props;

  // Unwrap location if it's nested in an action object
  const location = "action" in props.location ? props.location.location : props.location;

  return (
    <IonTabs>
      <IonRouterOutlet>
        {/* Project routes should come first (more specific) */}
        <Route
          path={Routes.Project.pattern}
          render={(routeProps) => {
            return <ProjectRouter {...routeProps} location={location} />;
          }}
        />

        {/* Main tab routes */}
        <Route exact path={Routes.Projects()} component={ProjectsPage} />
        <Route exact path={Routes.UpcomingShifts()} component={UpcomingShiftsPage} />
        <Route exact path={Routes.CompletedShifts()} component={CompletedShiftsPage} />
        <Route exact path={Routes.Conversations()} component={ConversationsPage} />
        <Route exact path={Routes.ConversationInfo.pattern} component={ConversationInfoPage} />
        <Route exact path={Routes.Conversation.pattern} component={ConversationDetailPage} />
        <Route exact path={Routes.Settings()} component={SettingsPage} />

        <Redirect
          exact
          from={Routes.App()}
          to={isAdmin ? Routes.Conversations() : Routes.Projects()}
        />
      </IonRouterOutlet>

      <IonTabBar
        slot="bottom"
        className="border *:ion-p-2 *:plt-desktop:max-w-40 mobile-sm:min-h-16 *:mobile-md:ion-px-4"
      >
        {!isAdmin && (
          <IonTabButton
            tab="projects"
            href={Routes.Projects()}
            onClick={() => PreferredHaptics.impact()}
            className="group text-txt-primary"
          >
            <IonIcon
              icon={homeOutlineIcon}
              className="size-6 text-txt-primary group-[.tab-selected]:hidden"
            />
            <IonIcon
              icon={homeSolidIcon}
              className="hidden size-6 text-brand-500 group-[.tab-selected]:block"
            />
            <IonLabel className="hidden text-xs font-normal leading-5 text-txt-primary group-[.tab-selected]:text-brand-500 mobile-sm:block">
              {t("routes:projects.title")}
            </IonLabel>
          </IonTabButton>
        )}

        {!isAdmin && (
          <IonTabButton
            tab="upcoming"
            href={Routes.UpcomingShifts()}
            onClick={() => PreferredHaptics.impact()}
            className="group text-txt-primary"
          >
            <IonIcon
              icon={calendarIcon}
              className="size-6 text-txt-primary group-[.tab-selected]:hidden"
            />
            <IonIcon
              icon={calendarIcon}
              className="hidden size-6 text-brand-500 group-[.tab-selected]:block"
            />
            <IonLabel className="hidden text-xs font-normal text-txt-primary group-[.tab-selected]:text-brand-500 mobile-sm:block">
              {t("routes:upcoming-shifts.title")}
            </IonLabel>
          </IonTabButton>
        )}

        {!isAdmin && (
          <IonTabButton
            tab="completed"
            href={Routes.CompletedShifts()}
            onClick={() => PreferredHaptics.impact()}
            className="group text-txt-primary"
          >
            <IonIcon
              icon={completedIcon}
              className="size-6 text-txt-primary group-[.tab-selected]:hidden"
            />
            <IonIcon
              icon={completedFilledIcon}
              className="hidden size-6 text-brand-500 group-[.tab-selected]:block"
            />
            <IonLabel className="hidden text-xs font-normal text-txt-primary group-[.tab-selected]:text-brand-500 mobile-sm:block">
              {t("routes:completed-shifts.title")}
            </IonLabel>
          </IonTabButton>
        )}

        <IonTabButton
          tab="conversations"
          href={Routes.Conversations()}
          onClick={() => PreferredHaptics.impact()}
          className="group text-txt-primary"
        >
          <IonIcon
            icon={chatIcon}
            className="size-6 text-txt-primary group-[.tab-selected]:hidden"
          />
          <IonIcon
            icon={chatFilledIcon}
            className="hidden size-6 text-brand-500 group-[.tab-selected]:block"
          />
          <IonLabel className="hidden text-xs font-normal text-txt-primary group-[.tab-selected]:text-brand-500 mobile-sm:block">
            {t("routes:conversations.title")}
          </IonLabel>
        </IonTabButton>

        <IonTabButton tab="settings" href={Routes.Settings()} className="group text-txt-primary">
          <IonIcon
            icon={settingsIcon}
            className="size-6 text-txt-primary group-[.tab-selected]:hidden"
          />
          <IonIcon
            icon={settingsFilledIcon}
            className="hidden size-6 text-brand-500 group-[.tab-selected]:block"
          />
          <IonLabel className="hidden text-xs font-normal text-txt-primary group-[.tab-selected]:text-brand-500 mobile-sm:block">
            {t("routes:settings.title")}
          </IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
}
